import { useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { searchUsers, searchInvitedUsers } from '@reducers/membership';
import { formatUser } from '@helpers';
import { buildSettingsNavParams, type SettingsRootStack } from '@components/settings/v2/common/navigationParams';
import type { RootState } from '@store/types';
import type { WithColor } from '@styles/colors';

export interface UseManageUsersParams extends WithColor {
  toggleInviteUser: () => void;
  rootStack?: SettingsRootStack;
}

export interface UseManageUsersReturn {
  formattedUsers: ReturnType<typeof formatUser>[];
  formattedInvitedUsers: ReturnType<typeof formatUser>[];
  loaded: boolean;
  invitedLoaded: boolean;
  isUsersMore: boolean;
  isInvitedUsersMore: boolean;
  dispatchSearchUsers: (params?: any) => void;
  dispatchSearchInvitedUsers: (params?: any) => void;
  navigateToUserDetails: (id: string) => void;
  toggleInviteUser: () => void;
}

export const useManageUsers = ({ toggleInviteUser, rootStack = 'Settings' }: UseManageUsersParams): UseManageUsersReturn => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation();

  const users = useSelector((state: RootState) => state.membership?.users ?? []);
  const loaded = useSelector((state: RootState) => state.membership?.loaded ?? false);
  const isUsersMore = useSelector((state: RootState) => state.membership?.isUsersMore ?? false);
  const invitedUsers = useSelector((state: RootState) => state.membership?.invitedUsers ?? []);
  const isInvitedUsersMore = useSelector((state: RootState) => state.membership?.isInvitedUsersMore ?? false);
  const invitedLoaded = useSelector((state: RootState) => state.membership?.invitedLoaded ?? false);

  const dispatchSearchUsers = useCallback(
    (params?: any) => dispatch(searchUsers(params)),
    [dispatch],
  );

  const dispatchSearchInvitedUsers = useCallback(
    (params?: any) => dispatch(searchInvitedUsers(params)),
    [dispatch],
  );

  useEffect(() => {
    dispatchSearchInvitedUsers({ reset: true });
    dispatchSearchUsers({ reset: true });
  }, []);

  const formattedUsers = useMemo(() => users.map((u: any) => formatUser(u)), [users]);
  const formattedInvitedUsers = useMemo(() => invitedUsers.map((u: any) => formatUser(u)), [invitedUsers]);

  const navigateToUserDetails = useCallback(
    (id: string) => {
      navigation.navigate(rootStack as never, buildSettingsNavParams(rootStack, 'Users', 'UserDetails', { id }) as never);
    },
    [navigation, rootStack],
  );

  return {
    formattedUsers,
    formattedInvitedUsers,
    loaded,
    invitedLoaded,
    isUsersMore,
    isInvitedUsersMore,
    dispatchSearchUsers,
    dispatchSearchInvitedUsers,
    navigateToUserDetails,
    toggleInviteUser,
  };
};
